import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { listApiKeys, createApiKey, revokeApiKey, API_PUBLIC_ROOT } from '../services/api';
import { resendVerificationEmail } from '../services/firebase';
import './ApiAccessSection.css';

const DEFAULT_LIMITS = { requestsPerMinute: 10, requestsPerMonth: 1000, activeKeysPerAccount: 1 };

const formatDateTime = (iso) => {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const requestExample = (key) => `curl -X POST ${API_PUBLIC_ROOT}/v1/track \\
  -H "X-API-Key: ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{"trackingId": "MSCU1234567"}'`;

/**
 * Developer API access: create the account's single key, see its prefix and
 * last use, revoke it, and copy a working request.
 *
 * The plaintext key exists only in this component's state, only between
 * creation and dismissal — the server stores a hash and can never return it
 * again, so the copy-once panel is the user's single chance to save it.
 */
export default function ApiAccessSection() {
  const { user, isEmailVerified, recheckEmailVerification } = useAuth();

  const [keys, setKeys] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [label, setLabel] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState('');
  const [confirmingRevokeId, setConfirmingRevokeId] = useState(null);

  const activeKey = keys.find((key) => !key.revoked) || null;

  const loadKeys = useCallback(async () => {
    if (!isEmailVerified) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await listApiKeys();
      setKeys(data.keys || []);
      if (data.limits) setLimits(data.limits);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load your API keys.');
    } finally {
      setIsLoading(false);
    }
  }, [isEmailVerified]);

  useEffect(() => {
    if (user) loadKeys();
  }, [user, loadKeys]);

  const copyToClipboard = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      setError('Could not copy to the clipboard. Select the text and copy it manually.');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setIsWorking(true);
    try {
      const created = await createApiKey(label.trim());
      setNewKey(created);
      setLabel('');
      await loadKeys();
    } catch (err) {
      setError(err.message || 'Could not create an API key.');
    } finally {
      setIsWorking(false);
    }
  };

  const handleRevoke = async (keyId) => {
    setError('');
    setNotice('');
    setIsWorking(true);
    try {
      await revokeApiKey(keyId);
      setConfirmingRevokeId(null);
      // The revoked key's secret is dead; drop it from the page too.
      setNewKey(null);
      setNotice('Key revoked. Requests using it will now be rejected.');
      await loadKeys();
    } catch (err) {
      setError(err.message || 'Could not revoke the key.');
    } finally {
      setIsWorking(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    try {
      await resendVerificationEmail();
      setNotice('Verification email sent. Check your inbox and spam folder.');
    } catch {
      setError('Could not resend the verification email. Please try again.');
    }
  };

  /* ── Unverified accounts cannot hold a key ──────────────────────────────── */
  if (!isEmailVerified) {
    return (
      <div className="profile-card api-access">
        <div className="api-access-head">
          <h2 className="profile-section-title">API Access</h2>
        </div>
        <div className="api-access-locked">
          <p className="api-access-locked-title">Verify your email to unlock API access</p>
          <p className="api-access-locked-body">
            We sent a verification link to <strong>{user?.email}</strong>. Once you confirm your
            address you can create an API key and call the tracking API from your own systems.
          </p>
          {notice && <p className="api-access-notice">{notice}</p>}
          {error && <p className="api-access-error">{error}</p>}
          <div className="api-access-actions">
            <button className="api-access-btn" onClick={recheckEmailVerification}>
              I've verified my email
            </button>
            <button className="api-access-btn ghost" onClick={handleResendVerification}>
              Resend email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-card api-access">
      <div className="api-access-head">
        <h2 className="profile-section-title">API Access</h2>
        <div className="api-access-head-actions">
          <a className="api-access-try-link" href="/developers#try-api">
            Try it <span aria-hidden="true">→</span>
          </a>
          <a
            className="api-access-docs-link"
            href={`${API_PUBLIC_ROOT}/api-docs`}
            target="_blank"
            rel="noreferrer"
          >
            API documentation <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>

      <p className="api-access-intro">
        Call the tracking API from your own systems with an API key. Keys work only from your
        server — never ship one to a browser or a mobile app.
      </p>

      <ul className="api-access-limits">
        <li><strong>{limits.requestsPerMinute}</strong> requests / minute</li>
        <li><strong>{limits.requestsPerMonth.toLocaleString()}</strong> requests / month</li>
        <li><strong>{limits.activeKeysPerAccount}</strong> active key per account</li>
      </ul>

      {notice && <p className="api-access-notice">{notice}</p>}
      {error && <p className="api-access-error">{error}</p>}

      {/* ── One-time secret reveal ──────────────────────────────────────── */}
      {newKey?.key && (
        <div className="api-access-reveal">
          <p className="api-access-reveal-title">Copy your key now — it is shown only once</p>
          <p className="api-access-reveal-body">
            We store only a hash of this key, so it cannot be shown again. If you lose it, revoke
            the key and create a new one.
          </p>
          <div className="api-access-secret">
            <code>{newKey.key}</code>
            <button
              className="api-access-btn"
              aria-label={copied === 'key' ? 'API key copied' : 'Copy API key'}
              onClick={() => copyToClipboard(newKey.key, 'key')}
            >
              {copied === 'key' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button className="api-access-btn ghost" onClick={() => setNewKey(null)}>
            I've saved it
          </button>
        </div>
      )}

      {/* ── Key list / creation ─────────────────────────────────────────── */}
      {isLoading ? (
        <div className="profile-loading">
          <div className="profile-spinner" />
          <p>Loading API keys…</p>
        </div>
      ) : activeKey ? (
        <div className="api-access-key">
          <div className="api-access-key-row">
            <div>
              <p className="api-access-key-label">{activeKey.label}</p>
              <code className="api-access-key-prefix">{activeKey.prefix}…</code>
            </div>
            {confirmingRevokeId === activeKey.id ? (
              <div className="api-access-actions">
                <button
                  className="api-access-btn danger"
                  onClick={() => handleRevoke(activeKey.id)}
                  disabled={isWorking}
                >
                  {isWorking ? 'Revoking…' : 'Confirm revoke'}
                </button>
                <button
                  className="api-access-btn ghost"
                  onClick={() => setConfirmingRevokeId(null)}
                  disabled={isWorking}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="api-access-btn danger-outline"
                onClick={() => setConfirmingRevokeId(activeKey.id)}
              >
                Revoke
              </button>
            )}
          </div>

          {confirmingRevokeId === activeKey.id && (
            <p className="api-access-warning">
              Revoking is permanent. Any system using this key will start receiving 403 errors
              immediately.
            </p>
          )}

          <dl className="api-access-meta">
            <div><dt>Created</dt><dd>{formatDateTime(activeKey.createdAt)}</dd></div>
            <div><dt>Last used</dt><dd>{formatDateTime(activeKey.lastUsedAt)}</dd></div>
          </dl>
        </div>
      ) : (
        <form className="api-access-create" onSubmit={handleCreate}>
          <label className="api-access-field">
            <span className="api-access-field-label">Key label</span>
            <input
              type="text"
              value={label}
              maxLength={60}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Production server"
            />
          </label>
          <button type="submit" className="api-access-btn" disabled={isWorking}>
            {isWorking ? 'Creating…' : 'Create API key'}
          </button>
        </form>
      )}

      {/* ── Copyable request example ────────────────────────────────────── */}
      <div className="api-access-example">
        <div className="api-access-example-head">
          <span className="api-access-field-label">Example request</span>
          <button
            className="api-access-btn ghost"
            aria-label={copied === 'example' ? 'Example request copied' : 'Copy example request'}
            onClick={() =>
              copyToClipboard(requestExample(newKey?.key || 'YOUR_API_KEY'), 'example')
            }
          >
            {copied === 'example' ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre className="api-access-code">
          <code>{requestExample(newKey?.key || 'YOUR_API_KEY')}</code>
        </pre>
      </div>

      {/* ── Revoked history ─────────────────────────────────────────────── */}
      {keys.some((key) => key.revoked) && (
        <details className="api-access-history">
          <summary>Revoked keys ({keys.filter((key) => key.revoked).length})</summary>
          <ul>
            {keys.filter((key) => key.revoked).map((key) => (
              <li key={key.id}>
                <code>{key.prefix}…</code>
                <span>{key.label}</span>
                <span>revoked {formatDateTime(key.revokedAt)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
